import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';

const MODEL_PATH = 'assets/model/formFestMap.glb';

/**
 * Meshes to hide after loading. Empty since the 25/08/2026 model — the export
 * is now visible-objects-only (scripts/blender-export-visible.py), so the
 * residual scatter this used to hide (stray CampingTent cone, 11 vehicle
 * nodes) no longer ships. The mechanism stays for future use.
 */
const HIDDEN_MESHES = [];

/**
 * Substrings that mark a mesh as genuinely needing DoubleSide rendering —
 * thin fabric, foliage cards, flags, water surface, paper-like props.
 * Everything else defaults to FrontSide, which halves fragment shader
 * invocations for closed solids like buildings, terrain, RVs, and roads.
 *
 * Any material with alphaTest > 0 (alpha-cutout foliage) is also forced to
 * DoubleSide regardless of name — that covers tree leaf cards reliably even
 * if the mesh naming doesn't match.
 */
const DOUBLE_SIDED_PATTERNS = [
  'Tent',        // CampingTents, glamping canvas
  'Tree',        // tree cards / foliage
  'Foliage',
  'Leaf',
  'Flag',
  'Banner',
  'Cloth',
  'Fabric',
  'Canopy',
  'Water',       // pool water (already custom shader, but belt-and-braces)
  'Cube001_4',   // explicit pool mesh name
];

/** Returns true if a mesh name or any of its materials require DoubleSide. */
function needsDoubleSide(mesh) {
  if (mesh.name && DOUBLE_SIDED_PATTERNS.some((p) => mesh.name.includes(p))) return true;
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  return mats.some((m) => m && m.alphaTest && m.alphaTest > 0);
}

/**
 * The self-shadowing terrain mesh — its back faces cast shadows so slopes shadow
 * themselves. Every other mesh casts from front faces (half the shadow fragments).
 */
const TERRAIN_SELFSHADOW_MESH = 'Terrain_Step_Terrace_CamCrop';

/**
 * Per-mesh render/shadow side. Centralised so material operations (tint, highlight,
 * dim, restore) preserve the FrontSide-default GPU optimisation and the terrain
 * BackSide self-shadow, instead of forcing every touched mesh to DoubleSide. These
 * are the source of truth — apply them anywhere a material is cloned/replaced.
 */
function intendedSide(mesh) {
  return needsDoubleSide(mesh) ? THREE.DoubleSide : THREE.FrontSide;
}
function intendedShadowSide(mesh) {
  return mesh && mesh.name === TERRAIN_SELFSHADOW_MESH ? THREE.BackSide : THREE.FrontSide;
}

/**
 * Meshes that must NOT cast shadows.
 * Roads/paths sit on the terrain — they receive shadows from buildings
 * but should never cast shadows themselves (avoids floating-road shadow artifacts).
 */
const NO_SHADOW_CAST = [
  'maposm_roads_residential',
  'Road_Trimmed',
  'Plane002',   // parking/road surface planes — same treatment as roads
  'Plane003',
];

let modelRoot = null;
let meshes = {};

/**
 * Load the GLB model with Draco support and progress tracking.
 * @param {THREE.Scene} scene
 * @param {(progress: number) => void} onProgress - 0..1
 * @returns {Promise<THREE.Group>}
 */
export async function loadModel(scene, onProgress) {
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath('/draco/');
  dracoLoader.setDecoderConfig({ type: 'js' });

  const loader = new GLTFLoader();
  loader.setDRACOLoader(dracoLoader);

  return new Promise((resolve, reject) => {
    loader.load(
      MODEL_PATH,
      (gltf) => {
        modelRoot = gltf.scene;

        // Enable shadows on all meshes and index them by name
        modelRoot.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;

            // Side mode: FrontSide by default — halves fragment shader work for
            // closed solids (terrain, buildings, RVs, roads). DoubleSide is
            // opted-in per mesh via DOUBLE_SIDED_PATTERNS or any material with
            // alphaTest > 0 (catches foliage cards). shadowSide is always
            // FrontSide — shadow maps only need front faces regardless.
            const sideMode = needsDoubleSide(child) ? THREE.DoubleSide : THREE.FrontSide;

            const mats = Array.isArray(child.material) ? child.material : [child.material];
            mats.forEach((mat) => {
              if (mat) {
                mat.side = sideMode;
                mat.shadowSide = THREE.FrontSide;
                // Fix Blender export artifact: opaque materials are often incorrectly
                // marked as transparent, causing render sorting issues.
                // EXCEPTION: materials with alphaTest > 0 (e.g. tree foliage) legitimately
                // need alpha cutout — don't strip transparency from those.
                if (mat.alphaTest === 0) {
                  mat.transparent = false;
                  mat.depthWrite = true;
                  mat.opacity = 1.0;
                }
              }
            });

            // Re-enable frustum culling — let the GPU skip off-screen meshes.
            // Large/offset geometry that was disappearing was likely a bounding
            // sphere issue; Three.js recomputes bounds on load so this should be fine.
            child.frustumCulled = true;

            // Store original material for highlight/restore, then replace with
            // an owned clone so no GLTF-shared materials remain. This makes
            // material disposal safe when highlighting/dimming categories.
            if (Array.isArray(child.material)) {
              child.userData.originalMaterial = child.material.map(m => m.clone());
              child.material = child.material.map(m => { const c = m.clone(); c.side = sideMode; c.shadowSide = THREE.FrontSide; return c; });
            } else {
              child.userData.originalMaterial = child.material.clone();
              const ownedMat = child.material.clone();
              ownedMat.side = sideMode;
              ownedMat.shadowSide = THREE.FrontSide;
              child.material = ownedMat;
            }

            // Index by name for config mapping
            if (child.name) {
              meshes[child.name] = child;
            }
          }
        });

        // Hide unwanted meshes
        HIDDEN_MESHES.forEach((name) => {
          const mesh = meshes[name];
          if (mesh) {
            mesh.visible = false;
            if (import.meta.env.DEV) console.log(`[Model] Hidden unwanted mesh: "${name}"`);
          }
        });

        // Disable shadow casting on road/path meshes
        NO_SHADOW_CAST.forEach((name) => {
          const mesh = meshes[name];
          if (mesh) {
            mesh.castShadow = false;
            if (import.meta.env.DEV) console.log(`[Model] Shadow cast disabled: "${name}"`);
          }
        });

        // Terrain self-shadowing fix: use BackSide shadow casting so the
        // natural offset between back faces and the lit surface lets
        // terrain slopes cast visible shadows on themselves.
        const terrain = meshes['Terrain_Step_Terrace_CamCrop'];
        if (terrain) {
          const mats = Array.isArray(terrain.material) ? terrain.material : [terrain.material];
          mats.forEach((mat) => { if (mat) mat.shadowSide = THREE.BackSide; });
        }

        // Scale correction: new model was exported at ~37× larger scale
        // (Blender cm vs m unit mismatch). Factor derived from old/new bounds ratio.
        // TODO: ask artist to re-export with "Apply Unit" enabled in Blender to remove this.
        modelRoot.scale.setScalar(0.027);

        scene.add(modelRoot);
        dracoLoader.dispose();

        // Force matrixWorld computation before the split (it's normally lazy-updated
        // on the first render, but we need correct world positions right now).
        modelRoot.updateMatrixWorld(true);

        // Split maposm_buildings011 into the GS sub-cluster and remainder
        split011Meshes();

        // Fix terraced paved areas that export ~2× brighter than the main terrain.
        // NOTE: Vert001/Vert001_1 are children of Placement_Tree_Maple, and
        // Vert002/Vert002_1 are children of Placement_Tree_Cyprus — do NOT touch them.
        const TERRAIN_COLOR = new THREE.Color(0.268, 0.260, 0.244);
        const makeTerrainMat = () => new THREE.MeshStandardMaterial({
          color: TERRAIN_COLOR,
          metalness: 0,
          roughness: 0.85,
          envMapIntensity: 0,
          side: THREE.DoubleSide,
          shadowSide: THREE.FrontSide,
        });

        [
          'Terrain_Terraced_CamCrop001', 'Terrain_Terraced_CamCrop002',
        ].forEach((name) => {
          const mesh = meshes[name];
          if (!mesh) return;
          const newMat = makeTerrainMat();
          mesh.material = newMat;
          mesh.userData.originalMaterial = newMat.clone();
        });

        if (import.meta.env.DEV) {
          console.log(`[Model] Loaded. Meshes indexed: ${Object.keys(meshes).length}`);
          console.log('[Model] Mesh names:', Object.keys(meshes));
        }

        if (onProgress) onProgress(1);
        resolve(modelRoot);
      },
      (xhr) => {
        if (xhr.total > 0 && onProgress) {
          onProgress(xhr.loaded / xhr.total);
        }
      },
      (err) => {
        console.error('[Model] Load failed:', err);
        reject(err);
      }
    );
  });
}


/**
 * Split a mesh's geometry into named parts: for each triangle, its world-space
 * (x,z) centroid is tested against each carve box in order — first match wins;
 * unmatched triangles fall into the `outside` bucket.
 * Returns { parts: { [key]: BufferGeometry|null }, outside: BufferGeometry|null }.
 *
 * The 011 OSM building mesh spans multiple physically separate sub-clusters.
 * This lets us isolate the Guest-Services building at load time without
 * requiring a Blender re-export.
 */
function splitGeometryByWorldBoxes(geometry, matrixWorld, carves) {
  const pos = geometry.attributes.position;
  const nor = geometry.attributes.normal;
  const uv  = geometry.attributes.uv;
  const idx = geometry.index;
  const mw  = matrixWorld.elements;

  const triCount = idx ? idx.count / 3 : pos.count / 3;

  const buckets = { outside: [] };
  for (const { key } of carves) buckets[key] = [];
  for (let t = 0; t < triCount; t++) {
    const i0 = idx ? idx.getX(t * 3)     : t * 3;
    const i1 = idx ? idx.getX(t * 3 + 1) : t * 3 + 1;
    const i2 = idx ? idx.getX(t * 3 + 2) : t * 3 + 2;
    // World-space centroid (x, z only needed for the box test)
    let cx = 0, cz = 0;
    for (const vi of [i0, i1, i2]) {
      const lx = pos.getX(vi), ly = pos.getY(vi), lz = pos.getZ(vi);
      cx += mw[0] * lx + mw[4] * ly + mw[8]  * lz + mw[12];
      cz += mw[2] * lx + mw[6] * ly + mw[10] * lz + mw[14];
    }
    cx /= 3; cz /= 3;
    let key = 'outside';
    for (const carve of carves) {
      const b = carve.box;
      if (cx >= b.xMin && cx <= b.xMax && cz >= b.zMin && cz <= b.zMax) { key = carve.key; break; }
    }
    buckets[key].push(i0, i1, i2);
  }

  function buildGeo(indices) {
    if (!indices.length) return null;
    const vertMap = new Map();
    const positions = [], normals = nor ? [] : null, uvs = uv ? [] : null;
    const newIdx = [];
    let nextV = 0;
    for (const vi of indices) {
      if (!vertMap.has(vi)) {
        vertMap.set(vi, nextV++);
        positions.push(pos.getX(vi), pos.getY(vi), pos.getZ(vi));
        if (normals) normals.push(nor.getX(vi), nor.getY(vi), nor.getZ(vi));
        if (uvs)     uvs.push(uv.getX(vi), uv.getY(vi));
      }
      newIdx.push(vertMap.get(vi));
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    if (normals) geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    if (uvs)     geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setIndex(newIdx);
    return geo;
  }

  const parts = {};
  for (const { key } of carves) parts[key] = buildGeo(buckets[key]);
  return { parts, outside: buildGeo(buckets.outside) };
}

/**
 * Split the maposm_buildings011 primitives into Guest-Services and remainder
 * meshes. The 011 GLTF node spans several physically separate building
 * sub-clusters (each prim covers the FULL extent — they are layers, not
 * separate buildings), so interactive buildings are carved by world box:
 *   - Guest Services: the L-shaped complex at the road junction (−4.56, −1.49)
 *
 * (The Soteria Safe Space cube was a second carve until 27/08/2026, when the
 * client removed the location and the artist deleted its geometry.)
 *
 * Mesh naming:
 *   maposm_buildings011_N      → kept as the remainder (indexed, not in modelMap)
 *   maposm_buildings011_N_gs   → GS-only portion (mapped in modelMap)
 */
function split011Meshes() {
  // World-space (post 0.027 scale) boxes. Cluster bounds measured from the
  // 25/08/2026 export: GS L-building x[-4.78..-4.43] z[-1.65..-1.34].
  const CARVES = [
    { key: 'gs', box: { xMin: -4.85, xMax: -4.35, zMin: -1.72, zMax: -1.28 } },
  ];

  ['maposm_buildings011_1', 'maposm_buildings011_2', 'maposm_buildings011_3'].forEach((name) => {
    const src = meshes[name];
    if (!src) return;

    const { parts, outside } = splitGeometryByWorldBoxes(src.geometry, src.matrixWorld, CARVES);

    function makeMesh(geo, newName) {
      if (!geo) return;
      const mat = Array.isArray(src.userData.originalMaterial)
        ? src.userData.originalMaterial.map(m => m.clone())
        : src.userData.originalMaterial.clone();
      const m = new THREE.Mesh(geo, mat);
      m.name = newName;
      // Apply the new mesh's intended side now that it has a name (building
      // footprints → FrontSide, same FrontSide-default optimisation as the rest).
      const side = intendedSide(m);
      const shadowSide = intendedShadowSide(m);
      (Array.isArray(m.material) ? m.material : [m.material]).forEach((x) => { x.side = side; x.shadowSide = shadowSide; });
      m.castShadow = true;
      m.receiveShadow = true;
      m.frustumCulled = true;
      m.userData.originalMaterial = Array.isArray(m.material) ? m.material.map(x => x.clone()) : m.material.clone();
      src.parent.add(m);
      meshes[newName] = m;
    }

    makeMesh(parts.gs, name + '_gs');
    makeMesh(outside,  name + '_nonGS');

    // Remove original split-source mesh from the scene and index;
    // its geometry is now covered by the new meshes.
    src.parent.remove(src);
    delete meshes[name];
  });
}

/**
 * Get a mesh by its GLTF object name.
 */
export function getMesh(name) {
  return meshes[name] || null;
}

/**
 * Get all indexed mesh names.
 */
export function getMeshNames() {
  return Object.keys(meshes);
}

/**
 * Get the model root group.
 */
export function getModelRoot() {
  return modelRoot;
}

/**
 * Dispose a mesh's current material(s), freeing GPU resources.
 * Skips originalMaterial and protectedMaterial to avoid double-free.
 */
function disposeMeshMaterial(mesh) {
  if (!mesh.material || mesh.userData.protectedMaterial) return;
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  mats.forEach((m) => { if (m && m.dispose) m.dispose(); });
}

/**
 * Clone a material (or array of materials), re-applying the mesh's intended
 * render side (FrontSide default, DoubleSide for fabric/foliage) and shadow side
 * (BackSide only for the self-shadowing terrain).
 */
function cloneMaterial(material, mesh) {
  const side = intendedSide(mesh);
  const shadowSide = intendedShadowSide(mesh);
  if (Array.isArray(material)) {
    return material.map((m) => {
      const c = m.clone();
      c.side = side;
      c.shadowSide = shadowSide;
      return c;
    });
  }
  const c = material.clone();
  c.side = side;
  c.shadowSide = shadowSide;
  return c;
}

/**
 * Tint meshes by lerping original material color toward a category color.
 * @param {string[]} names - GLTF object names
 * @param {number} color - Hex category color
 * @param {number} intensity - 0..1 blend factor (0 = original, 1 = full category color)
 */
export function tintMeshes(names, color, intensity) {
  const catColor = new THREE.Color(color);
  names.forEach((name) => {
    const mesh = meshes[name];
    if (!mesh || mesh.userData.protectedMaterial) return;
    const origMats = Array.isArray(mesh.userData.originalMaterial)
      ? mesh.userData.originalMaterial
      : [mesh.userData.originalMaterial];
    if (!origMats[0]) return;
    const side = intendedSide(mesh);
    const shadowSide = intendedShadowSide(mesh);
    const newMats = origMats.map((mat) => {
      const c = mat.clone();
      c.color.lerp(catColor, intensity);
      c.emissive.copy(catColor);
      c.emissiveIntensity = intensity * 0.2;
      c.side = side;
      c.shadowSide = shadowSide;
      return c;
    });
    disposeMeshMaterial(mesh);
    mesh.material = Array.isArray(mesh.userData.originalMaterial) ? newMats : newMats[0];
  });
}

/**
 * Highlight meshes by setting emissive color.
 * @param {string[]} names - GLTF object names
 * @param {number} color - Hex color (e.g., 0xFF6B35)
 */
export function highlightMeshes(names, color) {
  names.forEach((name) => {
    const mesh = meshes[name];
    if (!mesh) return;
    const side = intendedSide(mesh);
    const shadowSide = intendedShadowSide(mesh);
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const newMats = mats.map((mat) => {
      const c = mat.clone();
      c.color = new THREE.Color(color);
      c.emissive = new THREE.Color(color);
      c.emissiveIntensity = 0.2;
      c.side = side;
      c.shadowSide = shadowSide;
      return c;
    });
    disposeMeshMaterial(mesh);
    mesh.material = Array.isArray(mesh.material) ? newMats : newMats[0];
  });
}

/**
 * Restore all meshes to original materials.
 */
export function restoreAllMeshes() {
  Object.values(meshes).forEach((mesh) => {
    if (mesh.userData.protectedMaterial) return;
    if (mesh.userData.originalMaterial) {
      disposeMeshMaterial(mesh);
      mesh.material = cloneMaterial(mesh.userData.originalMaterial, mesh);
    }
  });
}

/**
 * Dim all meshes except specified ones.
 * @param {string[]} exceptNames - Mesh names to NOT dim
 * @param {number} dimAmount - 0..1 how much to darken
 */
export function dimMeshesExcept(exceptNames, dimAmount = 0.18) {
  const exceptSet = new Set(exceptNames);
  Object.entries(meshes).forEach(([name, mesh]) => {
    if (exceptSet.has(name)) return;
    if (mesh.userData.protectedMaterial) return;
    const side = intendedSide(mesh);
    const shadowSide = intendedShadowSide(mesh);
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const newMats = mats.map((mat) => {
      const c = mat.clone();
      c.color.multiplyScalar(1 - dimAmount);
      c.side = side;
      c.shadowSide = shadowSide;
      return c;
    });
    disposeMeshMaterial(mesh);
    mesh.material = Array.isArray(mesh.material) ? newMats : newMats[0];
  });
}
